import { colors, radius, sizes, spacings } from "@/assets/themes";
import {
  Avatar,
  Block,
  Body14,
  Body16,
  Body18,
  Caption,
  Divider,
  H3,
  H5,
  Icon,
  Link,
  Tag,
} from "@/components";
import { AVATAR_GROUP, TAG } from "@/components/Styles/variants";
import { useBreakpoints } from "@/modules/hooks";
import UserPreview from "@/pages-admin/UserPreview/UserPreview";
import { JOB } from "@/pages-admin/UserPreview/config";
import {
  OFFER_STATE_CANCELED,
  OFFER_STATE_PAID,
  OFFER_STATE_SELECTED,
} from "@/pages/Job/utils";
import polyglot from "@/utils/polyglot";
import { useMemo } from "react";
import { formatDate } from "@/utils";
import { artwork } from "@/assets/categories";
import JobContent from "./item/JobContent";

const JobsItem = ({
  id,
  title,
  cover,
  booked_offers,
  nb_hours,
  start_hour,
  subtitle,
  url,
  pending_offers_avatars,
  pending_offers_wording,
  pending_offers_title,
  recurring_job_id,
  category,
  date,
}) => {
  const breakpoints = useBreakpoints();

  const OfferState = ({ state }) => {
    if (state === OFFER_STATE_PAID)
      return (
        <Tag.Medium
          size={breakpoints.get({ xs: TAG.SIZE.SMALL, md: TAG.SIZE.MEDIUM })}
          kind={TAG.KIND.SUCCESS}
          LeftComponent={() => <Icon.Medium name="check-circle-solid" />}
        >
          {polyglot.t("common.paid")}
        </Tag.Medium>
      );
    if (state === OFFER_STATE_CANCELED)
      return (
        <Tag.Medium
          size={breakpoints.get({ xs: TAG.SIZE.SMALL, md: TAG.SIZE.MEDIUM })}
          kind={TAG.KIND.DANGER}
          LeftComponent={() => <Icon.Medium name="user-times-solid" />}
        >
          {polyglot.t("common.canceled")}
        </Tag.Medium>
      );
    if (state === OFFER_STATE_SELECTED)
      return (
        <Tag.Medium
          size={breakpoints.get({ xs: TAG.SIZE.SMALL, md: TAG.SIZE.MEDIUM })}
          kind={TAG.KIND.MUTED}
          LeftComponent={() => <Icon.Medium name="user-check-solid" />}
        >
          {polyglot.t("common.booked")}
        </Tag.Medium>
      );
    return null;
  };

  const getJobState = useMemo(
    () => (booked_offers) => {
      if (booked_offers?.some((offer) => offer.state === OFFER_STATE_PAID)) {
        return OFFER_STATE_PAID;
      }
      if (
        booked_offers?.some((offer) => offer.state === OFFER_STATE_SELECTED)
      ) {
        return OFFER_STATE_SELECTED;
      }
      if (
        booked_offers?.some((offer) => offer.state === OFFER_STATE_CANCELED)
      ) {
        return OFFER_STATE_CANCELED;
      }
      return null;
    },
    [booked_offers]
  );

  return (
    <UserPreview id={id} kind={JOB}>
      <Block
        borderRadius={radius.l}
        backgroundColor={colors.backgroundLight}
        padding={spacings.s}
        width="100%"
        display="flex"
        flexDirection="column"
        gap={spacings.s}
      >
        <Link
          href={url}
          color={colors.body}
          unstyled
          css={`
            display: block;
            &:hover,
            &:focus {
              [data-job-image] {
                transform: scale(1.1);
              }
            }
          `}
        >
          <Block display="flex" alignItems="stretch" width="100%">
            <Block position="relative">
              <Block
                width={{ xs: "112px", md: "220px" }}
                aspectRatio="1 / 1"
                borderRadius={radius.m}
                overflow="hidden"
              >
                <Block
                  data-job-image
                  width="100%"
                  height="100%"
                  backgroundImage={`url(${cover || artwork[category]})`}
                  backgroundRepeat="no-repeat"
                  backgroundSize="cover"
                  backgroundPosition="bottom right"
                  display="block"
                  backgroundColor={colors.gray100}
                  transition="transform 0.3s ease-in-out"
                />
              </Block>
              {date && (
                <Block
                  position="absolute"
                  top={0}
                  left={0}
                  width={sizes.size48}
                  paddingY={spacings.xs}
                  margin={spacings.xs}
                  backgroundColor={colors.backgroundBlur}
                  backdropBlur={20}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  borderRadius={radius.s}
                >
                  {breakpoints.get({
                    xs: (
                      <>
                        <Caption>{formatDate(date, "ddd")}</Caption>
                        <H5>{formatDate(date, "D")}</H5>
                        <Caption>{formatDate(date, "MMM")}</Caption>
                      </>
                    ),
                    md: (
                      <>
                        <Body14>{formatDate(date, "ddd")}</Body14>
                        <H3>{formatDate(date, "D")}</H3>
                        <Caption>{formatDate(date, "MMM")}</Caption>
                        {formatDate(date, "YYYY") !==
                          formatDate(new Date(), "YYYY") && (
                          <Body14>{formatDate(date, "YYYY")}</Body14>
                        )}
                      </>
                    ),
                  })}
                </Block>
              )}
              {typeof recurring_job_id === "number" && (
                <Block
                  position="absolute"
                  top={0}
                  right={0}
                  borderRadius={radius.circle}
                  margin={spacings.s}
                  padding={{ xs: spacings.xs, md: spacings.s }}
                  backgroundColor={colors.backgroundBlur}
                  backdropBlur={20}
                  aspectRatio="1 / 1"
                >
                  {breakpoints.get({
                    xs: (
                      <Icon.Small
                        name="refresh"
                        css={`
                          display: block;
                        `}
                      />
                    ),
                    md: (
                      <Icon.Large
                        name="refresh"
                        css={`
                          display: block;
                        `}
                      />
                    ),
                  })}
                </Block>
              )}
            </Block>

            <Block
              width="100%"
              display="flex"
              justifyContent={
                getJobState(booked_offers) ? "space-between" : "center"
              }
              flexDirection="column"
              paddingY={{ xs: "0px", md: spacings.m }}
              paddingRight={{ xs: spacings.s, md: spacings.sm }}
              paddingLeft={{ xs: spacings.m, md: spacings.ml }}
            >
              <Block spaceY={spacings.xs}>
                <Block display="flex" flexDirection="column" gap={spacings.xs}>
                  <div>
                    {breakpoints.get({
                      xs: (
                        <Body16 strong numberOfLines={1}>
                          {title}
                        </Body16>
                      ),
                      md: <H3 numberOfLines={subtitle ? 1 : 2}>{title}</H3>,
                    })}
                  </div>
                  <div>
                    {subtitle &&
                      breakpoints.get({
                        xs: (
                          <Body16
                            numberOfLines={2}
                            css={`
                              font-weight: normal;
                            `}
                          >
                            {subtitle}
                          </Body16>
                        ),
                        md: (
                          <Body18
                            numberOfLines={2}
                            css={`
                              font-weight: normal;
                            `}
                          >
                            {subtitle}
                          </Body18>
                        ),
                      })}
                  </div>
                </Block>
                {start_hour &&
                  nb_hours &&
                  breakpoints.get({
                    xs: (
                      <Block
                        display="flex"
                        alignItems="center"
                        gap={spacings.xs}
                        css={`
                          * {
                            font-weight: var(--font-weight-regular);
                          }
                        `}
                      >
                        <Body14>{polyglot.toDuration(start_hour, true)}</Body14>
                        <Icon.Small name="arrow-right" />
                        <Body14>
                          {polyglot.toDuration(
                            (start_hour + nb_hours) % 24,
                            true
                          )}
                        </Body14>
                      </Block>
                    ),
                    md: (
                      <Block
                        display="flex"
                        alignItems="center"
                        gap={spacings.xs}
                      >
                        <Body18>{polyglot.toDuration(start_hour, true)}</Body18>
                        <Icon.Medium name="arrow-right" />
                        <Body18>
                          {polyglot.toDuration(
                            (start_hour + nb_hours) % 24,
                            true
                          )}
                        </Body18>
                      </Block>
                    ),
                  })}
              </Block>
              {!!getJobState(booked_offers) && (
                <Block spaceY={spacings.sm}>
                  <Divider.Cell />
                  <Block
                    display="flex"
                    alignItems="center"
                    gap={spacings.s}
                    justifyContent="space-between"
                  >
                    <OfferState state={getJobState(booked_offers)} />
                    <Avatar.Group
                      outlineColor={colors.backgroundLight}
                      orientation={AVATAR_GROUP.ORIENTATION.HORIZONTAL}
                      max={2}
                    >
                      {booked_offers?.map((offer) => (
                        <Avatar
                          src={offer.jobber.avatar}
                          key={`offer.jobber-avatar${offer.jobber.id}`}
                          size={breakpoints.get({
                            xs: sizes.size24,
                            md: sizes.size36,
                          })}
                          name={offer.jobber.first_name}
                        />
                      ))}
                    </Avatar.Group>
                  </Block>
                </Block>
              )}
            </Block>
          </Block>
        </Link>
        <JobContent
          pending_offers_avatars={pending_offers_avatars}
          pending_offers_wording={pending_offers_wording}
          pending_offers_title={pending_offers_title}
        />
      </Block>
    </UserPreview>
  );
};

export default JobsItem;
